import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Constellation, Spinner, type ConstellationNode } from '@diwata/ironvale'
import { useAuth } from '@/store/auth'
import { buildFamiliarRoster, TIER_HUE, type RosterGroup } from './familiarRoster'

// The command centre for a familiar's ecosystem — the whole household drawn as one sky. The
// Sovereign holds the centre; the Adepts it delegates to and the peers it has bonded with orbit it.
// This is the AGENT graph rendered on the same ironvale Constellation primitive Diwa uses for its
// KNOWLEDGE graph: this file only maps Conclave's familiars/bonds onto the generic model and hands
// Conclave's semantics (tier hues, Inner/Outer orbits, navigation) in through props.

/** Sector/halo hues by group — matches the roster's tier tint so the legend and stars agree. */
function getGroupColor(group: string): string | undefined {
  return TIER_HUE[group as RosterGroup]
}

export function FamiliarConstellation() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()
  const navigate = useNavigate()

  const { data: familiar, isLoading: loadingFamiliar } = useQuery({
    queryKey: ['familiar', id],
    queryFn: () => api.getFamiliar(id!),
    enabled: !!id,
  })

  const isSovereign = familiar?.tier === 'sovereign'

  const [adeptsQ, bondsQ, allQ] = useQueries({
    queries: [
      // Adepts only delegate downward from a Sovereign; for other tiers this stays empty.
      { queryKey: ['adepts'], queryFn: () => api.listFamiliars('adept'), enabled: isSovereign },
      { queryKey: ['bonds', id], queryFn: () => api.listBonds(id!), enabled: !!id },
      // Every owned familiar — lets us name bonded counterparts we happen to own.
      { queryKey: ['familiars'], queryFn: () => api.listFamiliars() },
    ],
  })

  const roster = useMemo(() => {
    if (!familiar) return null
    const familiarsById = Object.fromEntries((allQ.data ?? []).map((f) => [f.id, f]))
    return buildFamiliarRoster({
      sovereign: familiar,
      adepts: isSovereign ? adeptsQ.data ?? [] : [],
      bonds: bondsQ.data ?? [],
      familiarsById,
    })
  }, [familiar, isSovereign, adeptsQ.data, bondsQ.data, allQ.data])

  // Clicking a star we own recentres the sky on it; external bonded familiars have no local page.
  const ownedIds = useMemo(() => new Set((allQ.data ?? []).map((f) => f.id)), [allQ.data])
  const onNodeClick = useCallback(
    (node: ConstellationNode) => {
      if (node.id !== id && ownedIds.has(node.id)) navigate(`/familiar/${node.id}/constellation`)
    },
    [id, ownedIds, navigate],
  )

  const theme = useMemo(
    () => ({
      ring: '#c9a96e', // gold constellation lines
      star: '#e8e2d9', // parchment star core
      starHalo: '#7a7268', // ash default halo
      focusCore: '#e0564a', // the Sovereign at centre wears the ego crimson
      focusHalo: '#f48a7f',
      label: '#e8e2d9',
      sector: '#7a7268',
    }),
    [],
  )

  if (loadingFamiliar || !roster || !familiar) {
    return <Spinner style={{ display: 'block', margin: '48px auto' }} />
  }

  const isEmpty = roster.adeptCount === 0 && roster.bondCount === 0

  return (
    <div>
      <div className="cp-constellation-intro">
        <p className="cp-page-subtitle">
          {familiar.name}&rsquo;s ecosystem — the household as one sky. Delegated{' '}
          <span style={{ color: TIER_HUE.adept }}>Adepts</span> and bonded{' '}
          <span style={{ color: TIER_HUE.bond }}>peers</span> orbit the centre.
          {!isEmpty && (
            <>
              {' '}
              {roster.adeptCount} adept{roster.adeptCount === 1 ? '' : 's'} · {roster.bondCount} bond
              {roster.bondCount === 1 ? '' : 's'}.
            </>
          )}{' '}
          Hover a star for its tier, orbit, and status; click one you own to recentre.
        </p>
      </div>

      <div className="cp-constellation-shell">
        <Constellation
          nodes={roster.nodes}
          edges={roster.edges}
          egoId={id}
          focusId={id}
          theme={theme}
          getGroupColor={getGroupColor}
          onNodeClick={onNodeClick}
          timeControl={false}
        >
          {isEmpty && (
            <div className="cp-sky-hint">
              <p className="cp-sky-hint-title">A constellation of one — for now</p>
              <p className="cp-sky-hint-copy">
                Bond a familiar or create an Adept to grow your constellation. Each one you add joins{' '}
                {familiar.name} as a new star.
              </p>
            </div>
          )}
        </Constellation>
      </div>
    </div>
  )
}
